import React, { useCallback } from 'react';
import { TableCell, TableRow, TableBody } from '@mui/material';
import makeStyles from '@mui/styles/makeStyles';
import { ColumnDefinitionType } from './type';
import TableCellRenderer from './TableCellRenderer';

const useTableCellStyles = makeStyles(() => ({
  tableCell: {
    borderBottom: 'none'
  }
}));

const useTableRowStyles = makeStyles(() => ({
  hover: {
    '&:hover': {
      cursor: 'pointer'
    }
  }
}));

type TableRowsProps<T, K extends keyof T> = {
  records: Array<T>;
  columns: Array<ColumnDefinitionType<T, K>>;
  onSelect: (value: T) => void;
};

type TableRowProps<T, K extends keyof T> = {
  record: T;
  columns: Array<ColumnDefinitionType<T, K>>;
  onSelect: (value: T) => void;
  rowIndex: number;
};

const Row = <T, K extends keyof T>({
  record,
  columns,
  onSelect,
  rowIndex
}: TableRowProps<T, K>): React.ReactElement => {
  const tableCellClasses = useTableCellStyles();
  const handleClick = useCallback(() => {
    onSelect(record);
  }, [record, onSelect]);
  return (
    <TableRow
      key={`row-${rowIndex}`}
      onClick={handleClick}
      classes={useTableRowStyles()}
      hover
      role="checkbox"
      tabIndex={-1}
    >
      {columns.map((column) => {
        return (
          <TableCell
            data-testid={`table-cell-${rowIndex}`}
            className={tableCellClasses.tableCell}
            key={column.id}
            align={column.align}
          >
            <TableCellRenderer
              component={column.render}
              field={column.key}
              column={column}
              record={record}
              index={rowIndex - 1}
            />
          </TableCell>
        );
      })}
    </TableRow>
  );
};

const TableRows = <T, K extends keyof T>({
  records,
  columns,
  onSelect
}: TableRowsProps<T, K>): React.ReactElement => {
  return (
    <TableBody>
      {records.map((record, rowIndex) => {
        return (
          <Row
            key={`row-${rowIndex}`}
            record={record}
            columns={columns}
            rowIndex={rowIndex}
            onSelect={onSelect}
          />
        );
      })}
    </TableBody>
  );
};

export default TableRows;
